import React, { useState, useEffect, useContext } from "react";
import { AuthContext, API } from "../App";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  CalendarIcon,
  Download,
  TrendingUp,
  Users,
  Calendar as CalendarDays,
  BarChart3,
  FileText,
  Building2,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { toast } from "sonner";
import { format, subDays, subWeeks, subMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isWeekend, parseISO, addMonths } from "date-fns";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';


export default function Reports() {
  const { user } = useContext(AuthContext);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [dateRange, setDateRange] = useState("week");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [monthData, setMonthData] = useState(null); // All records for current month (for busiest day)
  const [chartMonth, setChartMonth] = useState(new Date()); // Current month for chart navigation
  const [chartMonthData, setChartMonthData] = useState(null); // Data for the chart month
  const [nonWorkingDays, setNonWorkingDays] = useState([]); // Public holidays from non_working_days collection
  const [dayLabourReport, setDayLabourReport] = useState(null);
  const [selectedTab, setSelectedTab] = useState("overview");

  useEffect(() => {
    loadSites();
  }, []);

  useEffect(() => {
    if (selectedSite) {
      loadReports();
      loadMonthData(); // Load month data separately
      loadNonWorkingDays(); // Load public holidays
    }
  }, [selectedSite, dateRange, customStartDate, customEndDate]);

  // Load data for chart month when it changes
  useEffect(() => {
    if (selectedSite) {
      loadChartMonthData();
    }
  }, [chartMonth, selectedSite]);

  const loadSites = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/sites`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSites(data);
        if (data.length > 0) {
          setSelectedSite(data[0].id);
        }
      }
    } catch (error) {
      console.error("Error loading sites:", error);
      toast.error("Failed to load sites");
    }
  };

  const getDateRangeForFilter = () => {
    const now = new Date();
    
    switch (dateRange) {
      case "week":
        return {
          start: format(startOfWeek(now), "yyyy-MM-dd"),
          end: format(endOfWeek(now), "yyyy-MM-dd")
        };
      case "fortnight":
        return {
          start: format(subWeeks(now, 2), "yyyy-MM-dd"),
          end: format(now, "yyyy-MM-dd")
        };
      case "month":
        return {
          start: format(startOfMonth(now), "yyyy-MM-dd"),
          end: format(endOfMonth(now), "yyyy-MM-dd")
        };
      case "custom":
        return {
          start: customStartDate ? format(customStartDate, "yyyy-MM-dd") : format(subDays(now, 30), "yyyy-MM-dd"),
          end: customEndDate ? format(customEndDate, "yyyy-MM-dd") : format(now, "yyyy-MM-dd")
        };
      default:
        return {
          start: format(subWeeks(now, 1), "yyyy-MM-dd"),
          end: format(now, "yyyy-MM-dd")
        };
    }
  };

  const loadNonWorkingDays = async () => {
    if (!selectedSite) return;
    
    try {
      const token = localStorage.getItem("token");
      // Load non-working days for a wide range (entire year) to catch any retroactive toggles
      const currentYear = new Date().getFullYear();
      const startDate = `${currentYear - 1}-01-01`; // Previous year
      const endDate = `${currentYear + 1}-12-31`; // Next year
      
      const response = await fetch(
        `${API}/non-working-days?site_id=${selectedSite}&start_date=${startDate}&end_date=${endDate}`,
        { headers: { "Authorization": `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Store as array of date strings for the calculation
        setNonWorkingDays(data.map(d => d.date));
      }
    } catch (error) {
      console.error("Error loading non-working days:", error);
    }
  };

  const loadReports = async () => {
    if (!selectedSite) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { start, end } = getDateRangeForFilter();
      
      // Load day labour count report
      const dayLabourResponse = await fetch(
        `${API}/reports/day-labour-count?site_id=${selectedSite}&start_date=${start}&end_date=${end}`, 
        {
          headers: { "Authorization": `Bearer ${token}` }
        }
      );
      
      if (dayLabourResponse.ok) {
        const data = await dayLabourResponse.json();
        setDayLabourReport(data);
      }

      // Load general records for report data
      const recordsResponse = await fetch(
        `${API}/daily-records?site_id=${selectedSite}&start_date=${start}&end_date=${end}`, 
        {
          headers: { "Authorization": `Bearer ${token}` }
        }
      );
      
      if (recordsResponse.ok) {
        const recordsData = await recordsResponse.json();
        setReportData(recordsData);
      }
    } catch (error) {
      console.error("Error loading reports:", error);
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const loadMonthData = async () => {
    if (!selectedSite) return;
    
    try {
      const token = localStorage.getItem("token");
      const now = new Date();
      
      // Get first and last day of current month
      const startOfMonth = format(new Date(now.getFullYear(), now.getMonth(), 1), "yyyy-MM-dd");
      const endOfMonth = format(new Date(now.getFullYear(), now.getMonth() + 1, 0), "yyyy-MM-dd");
      
      // Load all records for the entire month
      const response = await fetch(
        `${API}/daily-records?site_id=${selectedSite}&start_date=${startOfMonth}&end_date=${endOfMonth}`, 
        {
          headers: { "Authorization": `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMonthData(data);
      }
    } catch (error) {
      console.error("Error loading month data:", error);
    }
  };

  const loadChartMonthData = async () => {
    try {
      const token = localStorage.getItem("token");
      const start = startOfMonth(chartMonth);
      const end = endOfMonth(chartMonth);
      const startStr = format(start, "yyyy-MM-dd");
      const endStr = format(end, "yyyy-MM-dd");

      const response = await fetch(
        `${API}/daily-records?site_id=${selectedSite}&start_date=${startStr}&end_date=${endStr}`,
        { headers: { "Authorization": `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setChartMonthData(data);
      }
    } catch (error) {
      console.error("Error loading chart month data:", error);
    }
  };

  // Calculate weekday average from given records - shared function for consistency
  const calculateWeekdayAverage = (records) => {
    if (!records || records.length === 0) return 0;
    
    // Filter out weekends (Saturday/Sunday) AND public holidays (non-working days)
    const workingDayRecords = records.filter(record => {
      const recordDate = parseISO(record.date);
      const dateStr = format(recordDate, 'yyyy-MM-dd');
      
      // Exclude if weekend
      if (isWeekend(recordDate)) {
        return false;
      }
      
      // Exclude if marked as public holiday in non_working_days
      if (nonWorkingDays.includes(dateStr)) {
        return false;
      }
      
      return true;
    });
    
    if (workingDayRecords.length === 0) return 0;
    
    // Calculate total personnel for working days only
    const totalWorkingDayPersonnel = workingDayRecords.reduce((sum, record) => {
      const dayLabourCount = record.day_labour 
        ? record.day_labour.reduce((acc, dl) => acc + (dl.number_of || 0), 0)
        : 0;
      const contractorCount = record.subcontractors 
        ? record.subcontractors.reduce((acc, sc) => acc + (sc.number_of_persons || 0), 0)
        : 0;
      return sum + dayLabourCount + contractorCount;
    }, 0);
    
    return Math.round(totalWorkingDayPersonnel / workingDayRecords.length);
  };

  const calculateStats = () => {
    if (!reportData || reportData.length === 0) {
      return {
        totalDays: 0,
        avgPeoplePerDay: 0,
        totalPeople: 0,
        weatherBreakdown: {},
        busiestDay: null
      };
    }

    const totalDays = reportData.length;
    
    // Calculate people dynamically from actual arrays
    const calculateRecordTotal = (record) => {
      const dlCount = record.day_labour 
        ? record.day_labour.reduce((sum, dl) => sum + (dl.number_of || 0), 0)
        : 0;
      const scCount = record.subcontractors 
        ? record.subcontractors.reduce((sum, sc) => sum + (sc.number_of_persons || 0), 0)
        : 0;
      return dlCount + scCount;
    };
    
    // Sum all people across all days in filtered range (day labour + contractors)
    const totalPeople = reportData.reduce((sum, record) => {
      return sum + calculateRecordTotal(record);
    }, 0);
    
    // Use the shared calculation method for consistency with graph
    const avgPeoplePerDay = calculateWeekdayAverage(reportData);

    const weatherBreakdown = reportData.reduce((acc, record) => {
      const weather = record.weather_condition;
      acc[weather] = (acc[weather] || 0) + 1;
      return acc;
    }, {});

    // Calculate busiest day from FILTERED data (reportData), not entire month
    const busiestDay = reportData && reportData.length > 0
      ? reportData.reduce((max, record) => {
          const currentTotal = calculateRecordTotal(record);
          const maxTotal = max ? calculateRecordTotal(max) : 0;
          return currentTotal > maxTotal ? record : max;
        }, null)
      : null;

    return {
      totalDays,
      avgPeoplePerDay,
      totalPeople,
      weatherBreakdown,
      busiestDay
    };
  };

  const stats = calculateStats();

  // Prepare data for daily personnel chart
  const getDailyPersonnelChartData = () => {
    if (!chartMonthData || chartMonthData.length === 0) return [];
    
    // Sort by date and create chart data
    return chartMonthData
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map(record => {
        const dayLabourCount = record.day_labour 
          ? record.day_labour.reduce((sum, dl) => sum + (dl.number_of || 0), 0)
          : 0;
        const contractorCount = record.subcontractors 
          ? record.subcontractors.reduce((sum, sc) => sum + (sc.number_of_persons || 0), 0)
          : 0;
        
        return {
          date: format(new Date(record.date), "dd MMM"),
          fullDate: record.date,
          "Day Labour": dayLabourCount,
          "Contractors": contractorCount
        };
      });
  };

  // Calculate average personnel for weekdays only (for graph)
  const getWeekdayAveragePersonnel = () => {
    return calculateWeekdayAverage(chartMonthData);
  };

  // Calculate weather breakdown for the chart month
  const getChartMonthWeatherBreakdown = () => {
    if (!chartMonthData || chartMonthData.length === 0) {
      return {};
    }
    
    return chartMonthData.reduce((acc, record) => {
      const weather = record.weather_condition;
      acc[weather] = (acc[weather] || 0) + 1;
      return acc;
    }, {});
  };

  const getWeatherIcon = (condition) => {
    const icons = {
      sunny: "☀️",
      moderate: "⛅",
      overcast: "☁️",
      windy: "💨",
      rain_lost_day: "🌧️",
      temperature_extreme: "🌡️"
    };
    return icons[condition] || "🌤️";
  };

  const getWeatherColor = (condition) => {
    const colors = {
      sunny: "text-yellow-700 bg-yellow-100",
      moderate: "text-blue-700 bg-blue-100",
      overcast: "text-gray-700 bg-gray-100",
      windy: "text-green-700 bg-green-100",
      rain_lost_day: "text-blue-800 bg-blue-200",
      temperature_extreme: "text-red-700 bg-red-100"
    };
    return colors[condition] || "text-gray-600 bg-gray-100";
  };

  const exportReport = () => {
    if (!reportData || !dayLabourReport) {
      toast.error("No data available to export");
      return;
    }

    const csvData = dayLabourReport.report_data.map(day => ({
      Date: day.date,
      "Total Day Labour": day.total_day_labour,
      "Direct Day Labour": day.day_labour_count,
      "Subcontractors": day.subcontractor_count,
      "Weather": day.weather_condition.replace('_', ' ')
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `day-labour-report-${selectedSite}-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    toast.success("Report exported successfully!");
  };

  const handleExportAllRecords = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/daily-records/export/csv`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error("Failed to export records");
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `all_daily_records_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("All daily records exported successfully!");
    } catch (error) {
      console.error("Error exporting records:", error);
      toast.error("Failed to export records");
    }
  };


  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 fade-in-up" data-testid="reports-container">
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="heading-primary text-2xl sm:text-4xl mb-2">Reports & Analytics</h1>
          <p className="text-sm sm:text-lg text-slate-600">Day labour tracking and site activity reports</p>
        </div>
        <Button
          onClick={handleExportAllRecords}
          className="btn-construction flex items-center gap-2 justify-center w-full sm:w-auto"
        >
          <Download className="w-4 h-4" />
          <span className="text-sm sm:text-base">Export All Records (CSV)</span>
        </Button>
      </div>

      {/* Filters */}
      <Card className="dashboard-card mb-8" data-testid="filters-card">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="font-medium text-sm text-slate-700">Site</label>
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger data-testid="report-site-select">
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="font-medium text-sm text-slate-700">Time Period</label>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger data-testid="date-range-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="fortnight">Last 2 Weeks</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {dateRange === "custom" && (
              <>
                <div className="space-y-2">
                  <label className="font-medium text-sm text-slate-700">Start Date</label>
                  <Popover open={showStartCalendar} onOpenChange={setShowStartCalendar}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {customStartDate ? format(customStartDate, "d MMMM yyyy") : "Select start date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={customStartDate}
                        onSelect={(date) => {
                          setCustomStartDate(date);
                          setShowStartCalendar(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <label className="font-medium text-sm text-slate-700">End Date</label>
                  <Popover open={showEndCalendar} onOpenChange={setShowEndCalendar}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {customEndDate ? format(customEndDate, "d MMMM yyyy") : "Select end date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={customEndDate}
                        onSelect={(date) => {
                          setCustomEndDate(date);
                          setShowEndCalendar(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end mt-4">
            <Button 
              onClick={exportReport} 
              className="btn-construction"
              disabled={!dayLabourReport || loading}
              data-testid="export-report-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Report
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center py-12" data-testid="loading-state">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading reports...</p>
        </div>
      ) : (
        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-6">
          {/* Mobile: Use Select Dropdown */}
          <div className="block sm:hidden mb-4">
            <Select value={selectedTab} onValueChange={setSelectedTab}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="overview">Overview</SelectItem>
                <SelectItem value="day-labour">Day Labour Details</SelectItem>
                <SelectItem value="daily">Daily Breakdown</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Desktop: Use Tabs */}
          <TabsList className="hidden sm:grid w-full grid-cols-3">
            <TabsTrigger value="overview" data-testid="overview-tab">Overview</TabsTrigger>
            <TabsTrigger value="day-labour" data-testid="day-labour-tab">Day Labour Details</TabsTrigger>
            <TabsTrigger value="daily" data-testid="daily-tab">Daily Breakdown</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              <Card className="dashboard-card" data-testid="total-days-stat">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs md:text-sm font-medium text-slate-600 mb-1">Total Days</p>
                      <p className="text-2xl md:text-3xl font-bold text-slate-900">{stats.totalDays}</p>
                    </div>
                    <div className="p-2 md:p-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl">
                      <CalendarDays className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="dashboard-card" data-testid="avg-day-labour-stat">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs md:text-sm font-medium text-slate-600 mb-1">Avg People/Day</p>
                      <p className="text-2xl md:text-3xl font-bold text-slate-900">{stats.avgPeoplePerDay}</p>
                    </div>
                    <div className="p-2 md:p-3 bg-gradient-to-r from-green-500 to-green-600 rounded-xl">
                      <Users className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="dashboard-card" data-testid="total-people-stat">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs md:text-sm font-medium text-slate-600 mb-1">Total People</p>
                      <p className="text-2xl md:text-3xl font-bold text-slate-900">{stats.totalPeople}</p>
                    </div>
                    <div className="p-2 md:p-3 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl">
                      <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="dashboard-card" data-testid="busiest-day-stat">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs md:text-sm font-medium text-slate-600 mb-1">Busiest Day</p>
                      <p className="text-xl md:text-2xl font-bold text-slate-900">
                        {stats.busiestDay ? (
                          (stats.busiestDay.day_labour 
                            ? stats.busiestDay.day_labour.reduce((sum, dl) => sum + (dl.number_of || 0), 0)
                            : 0
                          ) + (stats.busiestDay.subcontractors 
                            ? stats.busiestDay.subcontractors.reduce((sum, sc) => sum + (sc.number_of_persons || 0), 0)
                            : 0
                          )
                        ) : 0}
                      </p>
                      <p className="text-xs text-slate-500">
                        {stats.busiestDay ? format(new Date(stats.busiestDay.date), "dd MMM") : "No data"}
                      </p>
                    </div>
                    <div className="p-2 md:p-3 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl">
                      <BarChart3 className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Daily Personnel Chart */}
            <Card className="dashboard-card mt-6" data-testid="daily-personnel-chart">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="heading-secondary">Daily Personnel for the Month</CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setChartMonth(prev => subMonths(prev, 1))}
                      className="h-8"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm font-medium min-w-[120px] text-center">
                      {format(chartMonth, 'MMMM yyyy')}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setChartMonth(prev => addMonths(prev, 1))}
                      className="h-8"
                      disabled={format(chartMonth, 'yyyy-MM') >= format(new Date(), 'yyyy-MM')}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {getDailyPersonnelChartData().length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <p>No personnel data available for this month</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={getDailyPersonnelChartData()}
                      margin={{ top: 20, right: 100, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        label={{ value: 'Number of People', angle: -90, position: 'insideLeft' }}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: 'white', border: '1px solid #ccc' }}
                        labelStyle={{ fontWeight: 'bold' }}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '10px' }}
                        iconType="square"
                      />
                      <Bar 
                        dataKey="Day Labour" 
                        stackId="a" 
                        fill="#3b82f6"
                        radius={[0, 0, 0, 0]}
                      />
                      <Bar 
                        dataKey="Contractors" 
                        stackId="a" 
                        fill="#10b981"
                        radius={[4, 4, 0, 0]}
                      />
                      <ReferenceLine 
                        y={getWeekdayAveragePersonnel()} 
                        stroke="red" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        label={{
                          position: 'left',
                          content: ({ viewBox }) => {
                            const { x, y } = viewBox;
                            return (
                              <g>
                                <rect
                                  x={x + 10}
                                  y={y - 10}
                                  width={110}
                                  height={20}
                                  fill="white"
                                  stroke="red"
                                  strokeWidth={1}
                                  rx={4}
                                />
                                <text
                                  x={x + 65}
                                  y={y + 4}
                                  fill="red"
                                  fontSize={12}
                                  fontWeight="bold"
                                  textAnchor="middle"
                                >
                                  Weekday Avg: {getWeekdayAveragePersonnel()}
                                </text>
                              </g>
                            );
                          }
                        }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Weather Breakdown */}
            <Card className="dashboard-card" data-testid="weather-breakdown-card">
              <CardHeader>
                <CardTitle className="heading-secondary">
                  Weather Conditions Breakdown - {format(chartMonth, 'MMMM yyyy')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(getChartMonthWeatherBreakdown()).length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <p>No weather data available for {format(chartMonth, 'MMMM yyyy')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {Object.entries(getChartMonthWeatherBreakdown()).map(([condition, count]) => (
                      <div key={condition} className="text-center p-4 rounded-lg bg-slate-50">
                        <div className="text-2xl mb-2">{getWeatherIcon(condition)}</div>
                        <p className="font-semibold text-slate-900">{count} days</p>
                        <Badge className={`text-xs mt-1 ${getWeatherColor(condition)}`}>
                          {condition.replace('_', ' ')}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Day Labour Details Tab */}
          <TabsContent value="day-labour">
            <Card className="dashboard-card" data-testid="day-labour-report-card">
              <CardHeader>
                <CardTitle className="heading-secondary">Day Labour Count Report</CardTitle>
              </CardHeader>
              <CardContent>
                {!dayLabourReport || dayLabourReport.report_data.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No day labour data available for the selected period</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-2 px-4 font-medium text-slate-700">Date</th>
                            <th className="text-left py-2 px-4 font-medium text-slate-700">Weather</th>
                            <th className="text-right py-2 px-4 font-medium text-slate-700">Direct Day Labour</th>
                            <th className="text-right py-2 px-4 font-medium text-slate-700">Subcontractors</th>
                            <th className="text-right py-2 px-4 font-medium text-slate-700">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dayLabourReport.report_data.map((day, index) => (
                            <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-3 px-4 text-slate-900">
                                {format(new Date(day.date), "dd MMM yyyy")}
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center space-x-2">
                                  <span>{getWeatherIcon(day.weather_condition)}</span>
                                  <Badge className={`text-xs ${getWeatherColor(day.weather_condition)}`}>
                                    {day.weather_condition.replace('_', ' ')}
                                  </Badge>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-right text-slate-900 font-medium">
                                {day.day_labour_count}
                              </td>
                              <td className="py-3 px-4 text-right text-slate-900 font-medium">
                                {day.subcontractor_count}
                              </td>
                              <td className="py-3 px-4 text-right text-slate-900 font-bold">
                                {day.total}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Daily Breakdown Tab */}
          <TabsContent value="daily">
            <Card className="dashboard-card" data-testid="daily-breakdown-card">
              <CardHeader>
                <CardTitle className="heading-secondary">Daily Activity Records</CardTitle>
              </CardHeader>
              <CardContent>
                {!reportData || reportData.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No daily records available for the selected period</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {reportData.map((record) => (
                      <div key={record.id} className="p-6 border border-slate-200 rounded-lg bg-slate-50">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="font-semibold text-lg text-slate-900">
                              {format(new Date(record.date), "EEEE, dd MMMM yyyy")}
                            </h3>
                            <div className="flex items-center space-x-2 mt-1">
                              <span>{getWeatherIcon(record.weather_condition)}</span>
                              <Badge className={`text-xs ${getWeatherColor(record.weather_condition)}`}>
                                {record.weather_condition.replace('_', ' ')}
                              </Badge>
                              {record.temperature && (
                                <span className="text-sm text-slate-600">{record.temperature}°C</span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-slate-900">
                              {(record.day_labour 
                                ? record.day_labour.reduce((sum, dl) => sum + (dl.number_of || 0), 0)
                                : 0
                              ) + (record.subcontractors 
                                ? record.subcontractors.reduce((sum, sc) => sum + (sc.number_of_persons || 0), 0)
                                : 0
                              )}
                            </p>
                            <p className="text-sm text-slate-600">Total Personnel</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="font-medium text-slate-700 mb-1">Day Labour</p>
                            <p className="text-slate-600">
                              {record.day_labour 
                                ? record.day_labour.reduce((sum, dl) => sum + (dl.number_of || 0), 0)
                                : (record.personnel?.length || 0)
                              } people
                            </p>
                          </div>
                          <div>
                            <p className="font-medium text-slate-700 mb-1">Subcontractors</p>
                            <p className="text-slate-600">
                              {record.subcontractors 
                                ? record.subcontractors.reduce((sum, sc) => sum + (sc.number_of_persons || 0), 0)
                                : 0
                              } people
                            </p>
                          </div>
                          <div>
                            <p className="font-medium text-slate-700 mb-1">Deliveries</p>
                            <p className="text-slate-600">{record.deliveries?.length || 0} deliveries</p>
                          </div>
                          <div>
                            <p className="font-medium text-slate-700 mb-1">Equipment</p>
                            <p className="text-slate-600">{record.equipment?.length || 0} units</p>
                          </div>
                        </div>

                        {record.main_achievements && (
                          <div className="mt-4 pt-4 border-t border-slate-200">
                            <p className="font-medium text-slate-700 mb-2">Main Achievements</p>
                            <p className="text-slate-600 text-sm">{record.main_achievements}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}